import React, { useState, useEffect } from 'react';
import { Lock, LogOut, ClipboardCheck, ShoppingBag, FileUp, Download } from 'lucide-react';
import { api } from '../lib/api';
import DropZone from '../components/DropZone';
import { toast } from 'sonner';

const TOKEN_KEY = 'natur_student_token';
const fmtSize = (b) => (b < 1024 ? `${b} B` : b < 1024 * 1024 ? `${(b / 1024).toFixed(0)} KB` : `${(b / (1024 * 1024)).toFixed(1)} MB`);

export default function MiArea() {
  const [authed, setAuthed] = useState(() => !!sessionStorage.getItem(TOKEN_KEY));
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [me, setMe] = useState(null);
  const [orders, setOrders] = useState([]);
  const [docs, setDocs] = useState([]);
  const [uploading, setUploading] = useState(false);

  const loadAll = () => {
    api.portalMe().then(setMe).catch(() => { sessionStorage.removeItem(TOKEN_KEY); setAuthed(false); });
    api.portalOrders().then(setOrders).catch(() => {});
    api.portalDocuments().then(setDocs).catch(() => {});
  };

  useEffect(() => { if (authed) loadAll(); }, [authed]);

  const login = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { token, student } = await api.portalLogin(email, password);
      sessionStorage.setItem(TOKEN_KEY, token);
      setAuthed(true);
      toast.success(`Hola, ${student.name || student.email}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al iniciar sesión');
    } finally { setBusy(false); }
  };

  const logout = () => { sessionStorage.removeItem(TOKEN_KEY); setAuthed(false); setMe(null); };

  const uploadFiles = async (files) => {
    setUploading(true);
    try {
      for (const f of files) {
        await api.portalUpload(f);
        toast.success(`${f.name} subido correctamente`);
      }
      api.portalDocuments().then(setDocs).catch(() => {});
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al subir');
    } finally { setUploading(false); }
  };

  const download = async (d) => {
    try {
      const blob = await api.portalDocBlob(d.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = d.fileName; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('No se pudo descargar'); }
  };

  if (!authed) {
    return (
      <div className="max-w-md mx-auto px-4 py-16">
        <form onSubmit={login} className="bg-white rounded-xl shadow-lg border border-stone-200 p-8 text-center" data-testid="portal-login-form">
          <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Lock className="w-5 h-5 text-amber-600" />
          </div>
          <h1 className="text-xl font-black text-stone-800 mb-1">Área de Alumno</h1>
          <p className="text-sm text-stone-500 mb-6">Acceso exclusivo por invitación tras la aceptación de tu admisión.</p>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" autoComplete="username" className="w-full border border-stone-300 rounded px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 mb-3" data-testid="portal-email-input" />
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Contraseña" autoComplete="current-password" className="w-full border border-stone-300 rounded px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 mb-5" data-testid="portal-password-login" />
          <button type="submit" disabled={busy} className="btn-natur w-full py-3 rounded font-bold text-sm disabled:opacity-60" data-testid="portal-login-button">
            {busy ? 'Entrando…' : 'Entrar'}
          </button>
          <p className="text-[11px] text-stone-400 mt-4">¿Sin acceso? Recibirás un enlace de activación cuando tu admisión sea aceptada.</p>
        </form>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-10" data-testid="portal-dashboard">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-black text-stone-800">Hola, {me?.name || me?.email}</h1>
          <p className="text-sm text-stone-500">Tu área privada de alumno.</p>
        </div>
        <button onClick={logout} className="flex items-center gap-2 text-sm font-bold text-stone-500 hover:text-stone-800 transition-colors" data-testid="portal-logout">
          <LogOut className="w-4 h-4" /> Salir
        </button>
      </div>

      {me?.admission && (
        <div className="bg-white rounded-lg border border-stone-200 p-5 mb-6 flex items-center gap-4" data-testid="portal-admission-card">
          <ClipboardCheck className="w-8 h-8 text-teal-500 shrink-0" />
          <div>
            <p className="font-black text-stone-800 text-sm">Tu admisión: {me.admission.productName || me.admission.course || 'Solicitud de admisión'}</p>
            <p className="text-xs text-stone-500">Estado: <span className="font-bold text-teal-600 uppercase">{me.admission.status}</span></p>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg border border-stone-200 p-5 mb-6" data-testid="portal-orders">
        <h2 className="font-black text-stone-800 mb-3 flex items-center gap-2"><ShoppingBag className="w-4 h-4 text-stone-400" /> Mis pedidos</h2>
        {orders.length === 0 ? <p className="text-sm text-stone-400">No hay pedidos asociados a tu email todavía.</p> : (
          <div className="divide-y divide-stone-100">
            {orders.map((o) => (
              <div key={o.id} className="flex items-center gap-3 py-2.5 text-sm">
                <span className="font-bold text-stone-700">#{o.id}</span>
                <span className="text-stone-500 flex-1 truncate">{(o.items || []).map((i) => i.name).join(', ')}</span>
                <span className="text-stone-400 text-xs">{new Date(o.createdAt).toLocaleDateString('es-ES')}</span>
                <span className="font-black text-stone-800">{Number(o.total || 0).toFixed(2).replace('.', ',')} €</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="bg-white rounded-lg border border-stone-200 p-5" data-testid="portal-documents">
        <h2 className="font-black text-stone-800 mb-1 flex items-center gap-2"><FileUp className="w-4 h-4 text-stone-400" /> Mi documentación</h2>
        <p className="text-xs text-stone-500 mb-4">Sube aquí tu DNI, foto de carnet o certificados (máx. 10 MB por archivo).</p>
        <DropZone onFiles={uploadFiles} uploading={uploading} accept="image/*,.pdf" compact />
        {docs.length > 0 && (
          <div className="divide-y divide-stone-100 mt-4">
            {docs.map((d) => (
              <div key={d.id} className="flex items-center gap-3 py-2.5 text-sm" data-testid={`portal-doc-${d.id}`}>
                <span className="font-bold text-stone-700 flex-1 truncate">{d.fileName}</span>
                <span className="text-stone-400 text-xs">{fmtSize(d.size)} · {new Date(d.uploadedAt).toLocaleDateString('es-ES')}</span>
                <button onClick={() => download(d)} className="text-teal-600 hover:text-teal-800 transition-colors" title="Descargar" data-testid={`portal-doc-download-${d.id}`}>
                  <Download className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
